import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';

import { ProfileService } from '../profile/profile.service';
import { isProfileComplete } from './auth.types';

/**
 * Mirrors the legacy onboarding guard
 * (`growzz-web-app-legacy/app/app.config.js:69-80`): authenticated users
 * who haven't completed their profile (no `first_name`/`last_name` yet)
 * are bounced to the onboarding wizard.
 *
 * Runs after `authGuard`, so the user is guaranteed to have an Auth0
 * session when this fires.
 */
export const onboardingGuard: CanActivateFn = async (_route, state) => {
  const profile = inject(ProfileService);
  const router = inject(Router);

  try {
    await profile.ensureLoaded();
  } catch {
    // If `/users/me` fails outright, fall through and let the page render
    // with the cached error state — better UX than getting stuck on a
    // blank redirect target.
    return true;
  }

  if (isProfileComplete(profile.profile())) return true;

  return router.parseUrl(buildOnboardingUrl(state.url));
};

function buildOnboardingUrl(targetUrl: string): string {
  if (!targetUrl || targetUrl === '/' || targetUrl.startsWith('/onboarding')) {
    return '/onboarding';
  }
  return `/onboarding?redirect_url=${encodeURIComponent(targetUrl)}`;
}
